/**
 * Staff Form Dialog Component
 *
 * Modal dialog for creating and editing staff users.
 *
 * Features:
 * - Create new staff with email, password, name, and role
 * - Edit existing staff (name, role, store assignments)
 * - Role selection: admin, marketing, manager, staff
 * - Store assignment for manager/staff roles
 * - MFA enforcement toggle (force user to set up 2FA on next login)
 * - Email credentials option (send login details via SendGrid)
 *
 * Staff users are created with pre-verified email/phone status,
 * so they skip the customer verification flow.
 *
 * @see StaffTab - Parent component that renders this dialog
 * @see staff-controller.js - Backend API endpoints
 */

import { useState, useEffect } from 'react';
import { ROLE_CHOICES, choiceFor, choiceForUser, storesForChoice } from './staffPresets';
import PageAccessPicker from './PageAccessPicker';
import { TABLET_ACCESS_KEY } from '../../config/adminPages';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  Chip,
  OutlinedInput,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useQuery } from '@tanstack/react-query';
import authInstance from '../../services/httpClient';
import { queryKeys, useAdminMutation, extractErrorMessage } from '../../hooks/queries';
import { useAdminTheme } from '../../admin-theme';

interface Store {
  id: string;
  name: string;
  /** Lightspeed account: 'deeliver' or 'vaperite' (a Vaperite preset offers only its stores) */
  vendOrg?: string;
}

interface StaffUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  mfaEnabled?: boolean;
  mfaRequired?: boolean;
  pagePermissions?: string[] | null;
  tabletAdmin?: boolean;
  storeAssignments?: Array<{
    store: Store;
  }>;
}

interface StaffFormDialogProps {
  open: boolean;
  user: StaffUser | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const StaffFormDialog = ({ open, user, onClose, onSuccess }: StaffFormDialogProps) => {
  const { colors, elevatedInputStyle } = useAdminTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'staff',
    storeIds: [] as string[],
    mfaRequired: false,
    sendCredentials: true,
    tabletAdmin: false,
  });
  const [pagePermissions, setPagePermissions] = useState<string[] | null>(null);
  /** The role list's choice — a backend role plus the access preset it fills in (staffPresets). */
  const [choiceValue, setChoiceValue] = useState('staff');
  const [error, setError] = useState<string | null>(null);
  /** Set when the API said the email already belongs to a CUSTOMER: offer to promote them instead. */
  const [promotable, setPromotable] = useState<{ id: string } | null>(null);
  const notify = useNotify();

  const isEditMode = !!user;

  const { data: allStores = [] } = useQuery({
    queryKey: queryKeys.stores.list(),
    queryFn: async () => {
      const response = await authInstance.get('admin/stores');
      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Empty response from server — please refresh the page');
      }
      const arr = Array.isArray(response.data) ? response.data : (response.data.stores || response.data.data || []);
      return arr as Store[];
    },
    enabled: open,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
  const choice = choiceFor(choiceValue);
  const stores = storesForChoice(choice, allStores);

  useEffect(() => {
    if (open) {
      if (user) {
        setFormData({
          email: user.email,
          password: '',
          firstName: user.firstName,
          lastName: user.lastName,
          role: choiceForUser(user.roles, user.pagePermissions ?? null).role,
          storeIds: user.storeAssignments?.map((a) => a.store.id) || [],
          mfaRequired: user.mfaRequired || false,
          sendCredentials: false,
          tabletAdmin: user.tabletAdmin === true,
        });
        setPagePermissions(user.pagePermissions && user.pagePermissions.length > 0 ? user.pagePermissions : null);
        setChoiceValue(choiceForUser(user.roles, user.pagePermissions ?? null).value);
      } else {
        setFormData({
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          role: 'staff',
          storeIds: [],
          mfaRequired: false,
          sendCredentials: true,
          tabletAdmin: false,
        });
        setPagePermissions(null);
        setChoiceValue('staff');
      }
      setError(null);
      setPromotable(null);
    }
  }, [open, user]);

  const submitMutation = useAdminMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEditMode) {
        return authInstance.put(`admin/users/staff/${user!.id}`, payload);
      }
      return authInstance.post('admin/users/staff', payload);
    },
    successMessage: (resp: any) => {
      if (isEditMode) return 'Staff user updated successfully';
      return resp?.data?.credentialsSent
        ? 'Staff user created and credentials sent via email'
        : 'Staff user created successfully';
    },
    onSuccess: () => { onSuccess(); },
    onError: (err) => {
      const message = extractErrorMessage(err);
      setError(message);
      // "x is already registered as a customer (id <uuid>) — promote that account…":
      // turn the dead end into the action it names.
      const m = /registered as a customer \(id ([0-9a-f-]{36})\)/i.exec(message);
      setPromotable(m ? { id: m[1] } : null);
    },
  });

  const promoteMutation = useAdminMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      authInstance.post(`admin/users/customers/${promotable!.id}/promote`, payload),
    successMessage: () => `Customer promoted to ${formData.role} — they keep their customer account`,
    onSuccess: () => { onSuccess(); },
    onError: (err) => { setError(extractErrorMessage(err)); },
  });

  const loading = submitMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEditMode && !formData.password) {
      setError('Password is required for new users');
      return;
    }
    // The API's own floor (createStaff: minLength 8) — say so before sending.
    if (!isEditMode && formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setError(null);

    const payload: Record<string, unknown> = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      role: formData.role,
      storeIds: formData.storeIds,
      mfaRequired: formData.mfaRequired,
      // Tablet accounts carry exactly the tablet tick; their only other switch is Tablet admin.
      tabletAdmin: choice.role === 'tablet' ? formData.tabletAdmin : false,
      // [] (switch on, nothing ticked) is sent as null: the API treats both as
      // "role default", and an empty list must never lock someone out.
      pagePermissions: choice.role === 'tablet'
        ? [TABLET_ACCESS_KEY]
        : (pagePermissions && pagePermissions.length > 0 ? pagePermissions : null),
    };

    if (!isEditMode) {
      payload.email = formData.email;
      payload.password = formData.password;
      payload.sendCredentials = formData.sendCredentials;
    }

    submitMutation.mutate(payload);
  };

  const handleChange = (field: string, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const requiresStoreAssignment = choice.requiresStores;
  // An all-stores role (Admin, Marketing) can still carry store rows — the order tablet
  // reads them — so the picker shows whenever there are any, or tablet access is ticked.
  // Hiding it left shop@ stuck on its first 4 stores (2026-10-17).
  const showStorePicker = requiresStoreAssignment
    || formData.storeIds.length > 0
    || Boolean(user?.storeAssignments?.length)
    || Boolean(pagePermissions?.includes(TABLET_ACCESS_KEY));

  // Picking a role fills in its access preset (still editable below) and drops
  // stores the preset cannot be given — e.g. Deeliver stores for Staff – Vaperite.
  const selectChoice = (value: string) => {
    const next = choiceFor(value);
    const allowed = new Set(storesForChoice(next, allStores).map((st) => st.id));
    setChoiceValue(value);
    setFormData((prev) => ({ ...prev, role: next.role, storeIds: prev.storeIds.filter((id) => allowed.has(id)) }));
    setPagePermissions(next.pagePermissions ? [...next.pagePermissions] : null);
  };

  // "All stores" is a shortcut, not a flag: it selects every store, and the
  // backend gets one assignment row per store. A store created later must be
  // added by hand — the permission model (:own) has no wildcard.
  const ALL_STORES = '__all__';
  const allStoreIds = stores.map((s) => s.id);
  const hasAllStores = stores.length > 0 && allStoreIds.every((id) => formData.storeIds.includes(id));
  const handleStoreChange = (value: string[]) => {
    if (value.includes(ALL_STORES)) {
      handleChange('storeIds', hasAllStores ? [] : allStoreIds);
      return;
    }
    handleChange('storeIds', value);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          backgroundImage: 'none',
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ bgcolor: colors.bgElevated, color: colors.textPrimary }}>
          {isEditMode ? 'Edit Staff User' : 'Create Staff User'}
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                bgcolor: 'rgba(255, 0, 60, 0.1)',
                border: `1px solid ${colors.errorGlow}40`,
                color: colors.textPrimary,
                '& .MuiAlert-icon': { color: colors.errorGlow },
              }}
            >
              {error}
              {promotable && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={promoteMutation.isPending}
                    onClick={() => promoteMutation.mutate({
                      role: formData.role,
                      storeIds: formData.storeIds,
                      pagePermissions: pagePermissions && pagePermissions.length > 0 ? pagePermissions : null,
                    })}
                    data-testid="promote-customer"
                  >
                    Promote this customer to {formData.role}
                  </Button>
                </Box>
              )}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {!isEditMode && (
              <TextField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('email', e.target.value)}
                required
                fullWidth
                sx={elevatedInputStyle}
              />
            )}

            <TextField
              label="First Name"
              value={formData.firstName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('firstName', e.target.value)}
              required
              fullWidth
              sx={elevatedInputStyle}
            />

            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('lastName', e.target.value)}
              required
              fullWidth
              sx={elevatedInputStyle}
            />

            <FormControl fullWidth required sx={elevatedInputStyle}>
              <InputLabel>Role</InputLabel>
              <Select
                value={choiceValue}
                onChange={(e) => selectChoice(e.target.value as string)}
                label="Role"
                MenuProps={{ PaperProps: { sx: { bgcolor: colors.bgElevated, color: colors.textPrimary } } }}
              >
                {ROLE_CHOICES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
              <FormHelperText>{choice.description}</FormHelperText>
            </FormControl>

            {showStorePicker && (
              <FormControl fullWidth required={requiresStoreAssignment} sx={elevatedInputStyle}>
                <InputLabel>Assigned Stores</InputLabel>
                <Select
                  multiple
                  value={formData.storeIds}
                  onChange={(e) => handleStoreChange(e.target.value as string[])}
                  input={<OutlinedInput label="Assigned Stores" />}
                  MenuProps={{ PaperProps: { sx: { bgcolor: colors.bgElevated, color: colors.textPrimary } } }}
                  renderValue={(selected: any) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {hasAllStores ? (
                        <Chip
                          label={`All stores (${stores.length})`}
                          size="small"
                          sx={{
                            bgcolor: `${colors.info}20`,
                            color: colors.info,
                            border: `1px solid ${colors.info}40`
                          }}
                        />
                      ) : (selected as string[]).map((storeId: string) => {
                        const store = stores.find((s) => s.id === storeId);
                        return (
                          <Chip
                            key={storeId}
                            label={store?.name || storeId}
                            size="small"
                            sx={{
                              bgcolor: `${colors.info}20`,
                              color: colors.info,
                              border: `1px solid ${colors.info}40`
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {stores.length === 0 ? (
                    <MenuItem disabled>No stores available</MenuItem>
                  ) : (
                    [
                      <MenuItem key={ALL_STORES} value={ALL_STORES} sx={{ fontWeight: 700 }}>
                        {hasAllStores ? 'Clear all stores' : 'All stores'}
                      </MenuItem>,
                      <Divider key="all-stores-divider" />,
                      ...stores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.name}
                        </MenuItem>
                      )),
                    ]
                  )}
                </Select>
                <FormHelperText>
                  {requiresStoreAssignment
                    ? `${choice.label} users can only access orders and data from assigned stores`
                    : `${choice.label} users see every store in the dashboard; these are the stores their order tablet shows`}
                </FormHelperText>
              </FormControl>
            )}

            {choice.role === 'tablet' ? (
              <Box sx={{
                p: 2,
                bgcolor: colors.bgElevated,
                borderRadius: 1,
                border: `1px solid ${colors.border}`,
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.tabletAdmin}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('tabletAdmin', e.target.checked)}
                      inputProps={{ 'aria-label': 'tablet admin' }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500} sx={{ color: colors.textPrimary }}>
                        Tablet admin
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        Unlocks admin-only tablet features such as Test orders. Tick it for a store while they rehearse, then untick; they sign out and in to pick it up.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ) : (
              <PageAccessPicker value={pagePermissions} onChange={setPagePermissions} alwaysRestricted={formData.role === 'staff'} />
            )}

            {!isEditMode && (
              <TextField
                label="Password"
                type="password"
                value={formData.password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('password', e.target.value)}
                required
                fullWidth
                helperText="At least 8 characters. Change it later with Reset Password."
                sx={elevatedInputStyle}
              />
            )}

            {/* Send Credentials Option (only for new users) */}
            {!isEditMode && (
              <Box sx={{
                p: 2,
                bgcolor: 'rgba(0, 180, 255, 0.05)',
                borderRadius: 1,
                border: `1px solid ${colors.info}30`,
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.sendCredentials}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('sendCredentials', e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: colors.info },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.info },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500} sx={{ color: colors.textPrimary }}>
                        Email Login Credentials
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                        Send the user their login details via email
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* MFA Requirement Toggle */}
            <Box sx={{
              p: 2,
              bgcolor: colors.bgElevated,
              borderRadius: 1,
              border: `1px solid ${colors.border}`,
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.mfaRequired}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('mfaRequired', e.target.checked)}
                    disabled={user?.mfaEnabled}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: colors.warning },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.warning },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500} sx={{ color: colors.textPrimary }}>
                      Require MFA
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                      {user?.mfaEnabled
                        ? 'MFA is already enabled for this user'
                        : 'User must set up two-factor authentication on next login'
                      }
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: `1px solid ${colors.border}` }}>
          <Button onClick={onClose} disabled={loading} sx={{ color: colors.textSecondary }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
            sx={{
              bgcolor: colors.info,
              '&:hover': { bgcolor: `${colors.info}DD` }
            }}
          >
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
